import { PaymentType, ProductType, SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { ViewModes } from '@allesauto/utils/consts';
import { getFinanceMonthlyText } from '@allesauto/utils/funcs';
import { Routes_DE as Routes } from '@allesauto/utils/routes';
import { Popover, Transition } from '@headlessui/react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import { useRouter } from 'next/router';
import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getClassNamesForWLCartHeader } from './common/classNames';
import { getPlusNotification } from './common/funcs';
import Button from '../button';
import NextLink from '../nextLink';
import Price from '../price';
import VehicleImage from '../vehicleImage';
import { useWhiteLabel } from '../whiteLabel';

export type TradeInCarProps = {
  isTrade: boolean;
  tradeNameMark: string;
  tradeNameModel: string;
};

export interface HeaderCartMenuProps {
  open: boolean;
  items: SchemaTypes.CartItem[];
  handleHover: (flag: boolean) => void;
  link?: string;
  icon?: ReactNode;
  numOfNotification?: number;
  imageBaseUrls?: string;
  hideCartNotification?: () => void;
  className: string;
  whiteLabel?: boolean;
}

const HeaderCartMenu: FC<HeaderCartMenuProps> = ({
  open,
  items,
  handleHover,
  link,
  icon,
  numOfNotification = 0,
  hideCartNotification,
  className: popoverGroupClass,
  whiteLabel = false,
}) => {
  const { plusNotification } = getPlusNotification({
    numOfNotification,
  });
  const intl = useIntl();
  const router = useRouter();
  const MAX_PRODUCTS_NR = 10;
  const merchantOrigin = useWhiteLabel()?.merchantOrigin;

  const handleHoverOverlay = useCallback(
    (value: boolean) => {
      hideCartNotification?.();
      handleHover(value);
    },
    [handleHover, hideCartNotification]
  );

  const tradeInCar: TradeInCarProps | undefined = useMemo(() => {
    const tradeIn = [...items]
      .reverse()
      .find((item) => item.productType === ProductType.TradeInVehicle);

    if (!tradeIn) {
      return undefined;
    }

    const product = tradeIn.product as SchemaTypes.TradeInVehicle;

    return {
      isTrade: true,
      tradeNameMark: product.make,
      tradeNameModel: product.model,
    };
  }, [items]);

  const { className, priceClasses } = useMemo(
    () => getClassNamesForWLCartHeader(merchantOrigin),
    [merchantOrigin]
  );

  const handleShoppingCart = useCallback(async () => {
    await router.push(Routes.SHOPPING_CART);
  }, [router]);

  return (
    <Popover.Group
      onMouseLeave={() => {
        handleHoverOverlay(false);
      }}
      className={popoverGroupClass}
    >
      <Popover className={className.popoverClass}>
        <div
          className={clsx(
            open
              ? 'border-green70 monochrome:border-grey70 wl-border-highlight'
              : 'border-transparent monochrome:hover:border-grey70 hover:border-green70 wl-border-highlight--hover hover:border-b-7px',
            className.popoverButtonClass
          )}
          onKeyPress={() => {
            handleHoverOverlay(true);
          }}
          tabIndex={0}
          onMouseEnter={() => {
            handleHoverOverlay(true);
          }}
        >
          <div>
            {link && (
              <NextLink
                href={link}
                passHref={true}
                className={className.linkClass}
              >
                {icon}
                {numOfNotification > 0 && (
                  <span className={className.notificationClass}>
                    {plusNotification
                      ? '99'
                      : !!numOfNotification && numOfNotification}
                  </span>
                )}
              </NextLink>
            )}
          </div>
        </div>
        <Popover.Panel static className={className.popoverPanelClass}>
          <Transition
            enter="transition-[max-height] duration-250 ease"
            enterFrom="!max-h-0 overflow-hidden"
            enterTo="overflow-hidden max-h-[450px]"
            leave="transition-[max-height] duration-100 ease"
            leaveFrom="overflow-hidden max-h-[450px]"
            leaveTo="!max-h-0 overflow-hidden"
            show={open}
            className="bg-white border-1px border-grey10 flex-col overflow-hidden p-20px relative w-full sm:hidden lg:flex"
          >
            {items?.length > 0 && MAX_PRODUCTS_NR > 0 ? (
              <>
                <p className={className.popoverTitleClass}>
                  {translate(intl, 'ui.HEADER.MINI_SHOPPING_CART.TITLE')}
                </p>
                <div className="hide-scrollbar max-h-[430px] overflow-y-auto">
                  {items?.slice(0, MAX_PRODUCTS_NR).map((item, index) => {
                    if (
                      item.productType === ProductType.Vehicle ||
                      item.productType === ProductType.AccessoryStandAlone
                    ) {
                      const vehicle = item.product as SchemaTypes.Vehicle;
                      const accessory = item.product as SchemaTypes.Accessory;

                      return (
                        <NextLink
                          href={
                            item.productType === ProductType.Vehicle
                              ? `${Routes.PDP_VEHICLES}/${vehicle.makeName}/${vehicle.modelName}/${vehicle.id}`
                              : `${Routes.PDP_VEHICLES}`
                          }
                          key={index}
                          className={clsx(
                            'bg-white cursor-pointer group no-underline relative',
                            className.customLinkClass
                          )}
                        >
                          <div className="border-b-1px border-grey10 flex flex-row pt-20px w-full">
                            <div className="grid justify-center left-0 relative top-0 w-[35%]">
                              <div className="h-[60px] overflow-hidden w-[96px]">
                                <VehicleImage
                                  alt={
                                    item.productType === ProductType.Vehicle
                                      ? `${vehicle.makeName} ${vehicle.modelName}`
                                      : `${accessory.title}`
                                  }
                                  filename={
                                    item.productType === ProductType.Vehicle
                                      ? vehicle.images?.[0]
                                          ?.externalUrlLarge !== '/'
                                        ? vehicle.images?.[0]?.externalUrlLarge
                                        : undefined
                                      : accessory.images?.[0]?.url ?? undefined
                                  }
                                  fallbackImage="pcp_pdp"
                                  isWhiteLabel={whiteLabel}
                                />
                              </div>
                            </div>
                            <div className="flex flex-col pl-20px w-[65%]">
                              <p className="font-headline pb-20px text-left text-p2 text-purple80 monochrome:text-black">
                                {item.productType === ProductType.Vehicle
                                  ? `${vehicle.makeName} ${vehicle.modelName}`
                                  : accessory.title}
                              </p>
                              {!isNil(item.payment?.price) && (
                                <Price
                                  price={item.payment.price}
                                  monthlyPrice={
                                    item.payment.monthlyPrice ?? undefined
                                  }
                                  monthlyTextKey={
                                    item.payment.paymentType
                                      ? getFinanceMonthlyText(
                                          item.payment.paymentType
                                        )
                                      : undefined
                                  }
                                  displayView={ViewModes.GRID}
                                  priceTextKey="ui.PRICE.PRICE_ONLY"
                                  classNames={priceClasses}
                                  isMonthlyMainPrice={
                                    item.payment.paymentType !==
                                    PaymentType.CashRange
                                  }
                                  hasFractionDigits
                                />
                              )}
                            </div>
                          </div>
                        </NextLink>
                      );
                    }

                    return null;
                  })}
                  {tradeInCar?.isTrade && (
                    <>
                      <p className="font-headline pb-10px pt-25px text-grey100 text-headline4 text-left">
                        {translate(
                          intl,
                          'ui.HEADER.MINI_SHOPPING_CART.TRADE_IN'
                        )}
                      </p>
                      <div className="border-7px border-green60 p-20px text-left wl-border-highlight monochrome:border-grey90">
                        <p className="pb-10px text-grey100 text-p3">
                          {translate(
                            intl,
                            'ui.HEADER.MINI_SHOPPING_CART.TRADE_IN_TITLE'
                          )}
                        </p>
                        <p className="font-headline text-grey100 text-p3">
                          {tradeInCar.tradeNameMark} {tradeInCar.tradeNameModel}
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <Button
                  fullWidth={true}
                  text={translate(intl, 'ui.HEADER.MINI_SHOPPING_CART.BUTTON')}
                  customClassName="relative left-0 right-20px top-20px mb-20px monochrome:text-black wl-bg-highlight wl-border-highlight wl-bg-highlight--hover wl-text-highlight--hover wl-border-highlight--hover min-h-[50px]"
                  onClick={handleShoppingCart}
                />
              </>
            ) : (
              <p className={className.popoverTitleClass}>
                {translate(intl, 'ui.HEADER.MINI_SHOPPING_CART.EMPTY_CARD')}
              </p>
            )}
          </Transition>
        </Popover.Panel>
      </Popover>
    </Popover.Group>
  );
};

export default HeaderCartMenu;
